import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';

const breadcrumbItems = [
  { label: '首页', href: '/' },
  { label: '关于我们' },
];

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('website_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      setFormMessage('消息已发送，我们会尽快回复！');
      return;
    }

    const formData = new FormData(form);
    try {
      const response = await fetch('https://readdy.ai/api/form/d9spvip4ne17ei0sdm00', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言！我们会尽快回复。');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormStatus('error');
        setFormMessage(typeof msg === 'string' && (msg.includes('spam') || msg.includes('form data is spam')) ? msg : (msg || '发送失败，请稍后再试。'));
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请稍后再试。');
    }
  };

  const reputationStats = [
    { value: '120万+', label: '月活跃用户' },
    { value: '500+', label: '精选内容文章' },
    { value: '99.7%', label: '用户满意度' },
    { value: '3年', label: '持续运营时间' },
  ];

  const testimonials = [
    {
      name: '张先生',
      role: '影视爱好者',
      content: '欧美日韩一区是我每天必刷的平台，分类清晰，更新及时。无论是追美剧还是找日本电影推荐，都能在这里找到优质内容。',
      rating: 5,
    },
    {
      name: '李女士',
      role: '音乐发烧友',
      content: '作为一个K-POP粉丝和欧美音乐爱好者，欧美日韩一区完美满足了我的需求。精选推荐质量很高，文章深度也够。',
      rating: 5,
    },
    {
      name: '王同学',
      role: '大学生',
      content: '用来追日剧和日本动漫的首选导航站，分类做得特别好，一进来就能找到想看的。推荐给身边很多朋友了。',
      rating: 5,
    },
  ];

  return (
    <>
      <SchemaOrg
        type="AboutPage"
        data={{
          name: '关于我们 - 欧美日韩一区精选内容聚合平台',
          description: '欧美日韩一区汇聚全球优质影视、音乐、文化内容，致力于为用户提供最全面、最及时的精选内容导航服务。',
          url: 'https://www.oumeirihan.com/about',
        }}
      />

      <Navbar />
      <main className="pt-16 pb-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb items={breadcrumbItems} />

          {/* Hero */}
          <section className="mt-6 mb-16">
            <h1 className="text-2xl md:text-4xl font-bold font-heading text-foreground-900 mb-4">关于我们</h1>
            <p className="text-base md:text-lg text-foreground-600 max-w-3xl leading-relaxed">
              <strong>欧美日韩一区</strong>是一个专注于欧美、日本、韩国优质影视、音乐与文化内容的精选聚合平台。我们致力于为用户提供<strong>欧美日韩一区</strong>最全面的分类资源导航，让每一位文化娱乐爱好者都能在这里发现精彩。
            </p>
          </section>

          {/* Brand Introduction */}
          <section className="mb-16">
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-6">品牌介绍</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
              <div className="space-y-4">
                <p className="text-sm text-foreground-700 leading-relaxed">
                  <strong>欧美日韩一区</strong>诞生于2023年，由一群热爱全球文化娱乐内容的资深编辑与技术人员共同创立。我们注意到，中文互联网上缺少一个能够系统性地聚合欧美、日本、韩国优质影视音乐内容的平台——信息分散在各处，用户需要花费大量时间在不同网站之间跳转搜索。
                </p>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  于是<strong>欧美日韩一区</strong>应运而生。我们以"精选"为核心理念，不追求海量堆砌，而是由专业编辑团队筛选出真正值得关注的优质内容。每一篇推荐文章都经过深度调研与精心撰写，确保为读者提供有价值的信息与独到的见解。
                </p>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  目前，<strong>欧美日韩一区</strong>已覆盖9大内容分类，涵盖欧美电影、欧美剧集、欧美音乐、日本电影、日本剧集、日本音乐、韩国电影、韩国剧集、韩国音乐。我们持续追踪全球文化娱乐前沿动态，确保<strong>欧美日韩一区</strong>最新资源第一时间触达用户。
                </p>
              </div>
              <div className="rounded-xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20creative%20office%20workspace%20with%20large%20screens%20displaying%20film%20and%20music%20content%2C%20warm%20ambient%20lighting%2C%20minimalist%20design%20with%20wooden%20accents%2C%20collaborative%20atmosphere%2C%20editorial%20photography%20style%2C%20clean%20background%2C%20no%20text&width=700&height=450&seq=about-brand-2026&orientation=landscape"
                  alt="欧美日韩一区品牌介绍"
                  className="w-full h-full object-cover"
                  title="欧美日韩一区品牌介绍"
                />
              </div>
            </div>
          </section>

          {/* Reputation */}
          <section className="mb-16">
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-6">平台口碑</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
              {reputationStats.map((stat) => (
                <div
                  key={stat.label}
                  className="text-center p-6 rounded-xl bg-background-100 border border-background-200/60"
                >
                  <div className="text-2xl md:text-3xl font-bold text-primary-500 font-heading">{stat.value}</div>
                  <div className="text-sm text-foreground-500 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {testimonials.map((t) => (
                <div
                  key={t.name}
                  className="p-6 rounded-xl bg-background-50 border border-background-200/60"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">&ldquo;{t.content}&rdquo;</p>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">{t.name}</p>
                    <p className="text-xs text-foreground-500">{t.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Contact Form */}
          <section id="contact" className="mb-8">
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-6">联系我们</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
              <div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                  如果您对<strong>欧美日韩一区</strong>平台有任何建议、合作意向或内容投稿需求，欢迎通过以下方式联系我们。我们的团队将在1-2个工作日内回复您的消息。
                </p>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">电子邮箱</p>
                      <p className="text-sm text-foreground-500">contact@oumeirihan.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-wechat-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">微信公众号</p>
                      <p className="text-sm text-foreground-500">欧美日韩一区精选</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-bilibili-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">Bilibili</p>
                      <p className="text-sm text-foreground-500">@欧美日韩一区官方</p>
                    </div>
                  </div>
                </div>
              </div>

              <form
                onSubmit={handleContactSubmit}
                data-readdy-form
                className="p-6 rounded-xl bg-background-100 border border-background-200/60 space-y-4"
              >
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="absolute left-[-9999px] opacity-0 h-0 w-0 overflow-hidden pointer-events-none"
                />
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    姓名 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    id="contact-name"
                    type="text"
                    name="name"
                    required
                    placeholder="请输入您的姓名"
                    className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    邮箱 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    id="contact-email"
                    type="email"
                    name="email"
                    required
                    placeholder="请输入您的邮箱地址"
                    className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                  />
                </div>
                <div>
                  <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    主题
                  </label>
                  <input
                    id="contact-subject"
                    type="text"
                    name="subject"
                    placeholder="请输入留言主题"
                    className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                  />
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    留言内容 <span className="text-primary-500">*</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    placeholder="请输入您的留言内容（最多500字）"
                    className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                  ></textarea>
                </div>
                <button
                  type="submit"
                  className="w-full px-6 py-3 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                >
                  发送留言
                </button>
                {formStatus !== 'idle' && (
                  <p className={`text-sm text-center ${formStatus === 'success' ? 'text-primary-600' : 'text-red-500'}`}>
                    {formMessage}
                  </p>
                )}
              </form>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}